import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { colors } from '../styles/colors';

interface Subscription {
  id: number;
  candidate_id: number;
  active: boolean;
  canceled: boolean;
  canceled_at: string | null;
  cancelation_reason: string | null;
  activated_at: string | null;
  expires_at: string | null;
  created_at: string;
  invoices?: Invoice[];
}

interface Invoice {
  id: number;
  from_date: string | null;
  to_date: string | null;
  status: string | null;
  type: string | null;
  created_at: string;
}

interface CandidateSubscriptionsModalProps {
  candidateId: number;
  onClose: () => void;
}

const CandidateSubscriptionsModal: React.FC<CandidateSubscriptionsModalProps> = ({
  candidateId,
  onClose
}) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelingSubscriptionId, setCancelingSubscriptionId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSubscription, setNewSubscription] = useState({
    activated_at: '',
    expires_at: '',
    checkout_product_id: ''
  });
  const [products, setProducts] = useState<any[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    fetchSubscriptions();
  }, [candidateId]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/subscriptions?candidate_id=${candidateId}`);
      setSubscriptions(response.data.subscriptions || []);
    } catch (error) {
      toast.error('Erro ao carregar assinaturas');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async (id: number) => {
    setCancelingSubscriptionId(id);
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelingSubscriptionId || !cancelReason.trim()) {
      toast.error('Motivo é obrigatório');
      return;
    }

    try {
      await api.put(`/subscriptions/${cancelingSubscriptionId}/cancel`, { reason: cancelReason });
      toast.success('Assinatura cancelada com sucesso');
      fetchSubscriptions();
      setShowCancelModal(false);
      setCancelingSubscriptionId(null);
      setCancelReason('');
    } catch (error) {
      toast.error('Erro ao cancelar assinatura');
    }
  };

  const cancelCancel = () => {
    setShowCancelModal(false);
    setCancelingSubscriptionId(null);
    setCancelReason('');
  };

  const handleAddSubscription = () => {
    fetchProducts();
    setShowAddModal(true);
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/subscriptions/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  };

  const saveNewSubscription = async () => {
    if (!newSubscription.activated_at || !newSubscription.expires_at || !newSubscription.checkout_product_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      await api.post('/subscriptions', {
        candidate_id: candidateId,
        activated_at: newSubscription.activated_at,
        expires_at: newSubscription.expires_at,
        checkout_product_id: newSubscription.checkout_product_id,
        active: true
      });
      toast.success('Assinatura adicionada com sucesso');
      fetchSubscriptions();
      setShowAddModal(false);
      setNewSubscription({ activated_at: '', expires_at: '', checkout_product_id: '' });
    } catch (error) {
      toast.error('Erro ao adicionar assinatura');
    }
  };

  const cancelAdd = () => {
    setShowAddModal(false);
    setNewSubscription({ activated_at: '', expires_at: '', checkout_product_id: '' });
  };

  const handleEditSubscription = (subscription: Subscription) => {
    setEditingSubscription(subscription);
    setShowEditModal(true);
  };

  const saveEditSubscription = async () => {
    if (!editingSubscription?.expires_at) {
      toast.error('Data de expiração é obrigatória');
      return;
    }

    try {
      await api.put(`/subscriptions/${editingSubscription.id}`, {
        expires_at: editingSubscription.expires_at
      });
      toast.success('Assinatura atualizada com sucesso');
      fetchSubscriptions();
      setShowEditModal(false);
      setEditingSubscription(null);
    } catch (error) {
      toast.error('Erro ao atualizar assinatura');
    }
  };

  const cancelEdit = () => {
    setShowEditModal(false);
    setEditingSubscription(null);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>Assinaturas do Candidato</h2>
          <div style={styles.headerButtons}>
            <button onClick={handleAddSubscription} style={styles.addBtn}>
              Adicionar Assinatura
            </button>
            <button onClick={onClose} style={styles.closeBtn}>×</button>
          </div>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loading}>Carregando...</div>
          ) : subscriptions.length === 0 ? (
            <div style={styles.empty}>Nenhuma assinatura encontrada</div>
          ) : (
            subscriptions.map((subscription) => (
              <div key={subscription.id} style={styles.subscriptionCard}>
                <div style={styles.subscriptionHeader}>
                  <span style={styles.subscriptionId}>#{subscription.id}</span>
                  <span style={{
                    ...styles.status,
                    backgroundColor: subscription.canceled ? colors.error : colors.success
                  }}>
                    {subscription.canceled ? 'CANCELADA' : 'ATIVA'}
                  </span>
                </div>

                <div style={styles.subscriptionDetails}>
                  <div><strong>Ativada em:</strong> {formatDate(subscription.activated_at)}</div>
                  <div><strong>Expira em:</strong> {formatDate(subscription.expires_at)}</div>
                  <div><strong>Criada em:</strong> {formatDate(subscription.created_at)}</div>
                  
                  {subscription.canceled && (
                    <div style={styles.cancelInfo}>
                      <div><strong>Cancelada em:</strong> {formatDate(subscription.canceled_at)}</div>
                      {subscription.cancelation_reason && (
                        <div><strong>Motivo:</strong> {subscription.cancelation_reason}</div>
                      )}
                    </div>
                  )}
                </div>

                {subscription.invoices && subscription.invoices.length > 0 && (
                  <div style={styles.invoices}>
                    <h4>Faturas:</h4>
                    {subscription.invoices.map((invoice) => (
                      <div key={invoice.id} style={styles.invoice}>
                        <span>#{invoice.id}</span>
                        <span>{invoice.status}</span>
                        <span>{formatDate(invoice.created_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
                    <button
                      onClick={() => handleEditSubscription(subscription)}
                      style={styles.editBtn}
                    >
                      Editar Expiração
                    </button>
                {!subscription.canceled && (
                  <>
                    <button
                      onClick={() => handleCancelSubscription(subscription.id)}
                      style={styles.cancelBtn}
                    >
                      Cancelar Assinatura
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      
      {showCancelModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.cancelModal}>
            <h3 style={styles.cancelTitle}>Cancelar Assinatura</h3>
            <p>Informe o motivo do cancelamento:</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Motivo do cancelamento..."
              style={styles.textarea}
              rows={3}
            />
            <div style={styles.cancelButtons}>
              <button onClick={cancelCancel} style={styles.cancelBtn}>
                Cancelar
              </button>
              <button onClick={confirmCancel} style={styles.confirmBtn}>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showAddModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.addModal}>
            <h3 style={styles.addTitle}>Adicionar Assinatura</h3>
            <div style={styles.formGroup}>
              <label>Plano *</label>
              <select
                value={newSubscription.checkout_product_id}
                onChange={(e) => setNewSubscription(prev => ({ ...prev, checkout_product_id: e.target.value }))}
                style={styles.input}
              >
                <option value="">Selecione um plano</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name} {product.price && `- R$ ${product.price}`}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.formGroup}>
              <label>Data de Ativação *</label>
              <input
                type="datetime-local"
                value={newSubscription.activated_at}
                onChange={(e) => setNewSubscription(prev => ({ ...prev, activated_at: e.target.value }))}
                style={styles.input}
              />
            </div>
            <div style={styles.formGroup}>
              <label>Data de Expiração *</label>
              <input
                type="datetime-local"
                value={newSubscription.expires_at}
                onChange={(e) => setNewSubscription(prev => ({ ...prev, expires_at: e.target.value }))}
                style={styles.input}
              />
            </div>
            <div style={styles.addButtons}>
              <button onClick={cancelAdd} style={styles.cancelAddBtn}>
                Cancelar
              </button>
              <button onClick={saveNewSubscription} style={styles.saveBtn}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showEditModal && editingSubscription && (
        <div style={styles.modalOverlay}>
          <div style={styles.editModal}>
            <h3 style={styles.editTitle}>Editar Data de Expiração</h3>
            <div style={styles.formGroup}>
              <label>Data de Expiração *</label>
              <input
                type="datetime-local"
                value={editingSubscription.expires_at ? editingSubscription.expires_at.slice(0, 16) : ''}
                onChange={(e) => setEditingSubscription(prev => prev ? { ...prev, expires_at: e.target.value } : null)}
                style={styles.input}
              />
            </div>
            <div style={styles.editButtons}>
              <button onClick={cancelEdit} style={styles.cancelEditBtn}>
                Cancelar
              </button>
              <button onClick={saveEditSubscription} style={styles.saveEditBtn}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '80vh',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    borderBottom: `1px solid ${colors.border}`,
  },
  headerButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  addBtn: {
    padding: '8px 16px',
    backgroundColor: colors.secondary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  title: {
    margin: 0,
    color: colors.primary,
    fontSize: '20px',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: colors.textMuted,
  },
  content: {
    padding: '20px',
    maxHeight: '60vh',
    overflow: 'auto',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '40px',
    color: colors.textMuted,
  },
  empty: {
    textAlign: 'center' as const,
    padding: '40px',
    color: colors.textMuted,
  },
  subscriptionCard: {
    border: `1px solid ${colors.border}`,
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px',
  },
  subscriptionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  subscriptionId: {
    fontWeight: 'bold',
    color: colors.primary,
  },
  status: {
    padding: '4px 8px',
    borderRadius: '4px',
    color: 'white',
    fontSize: '12px',
    fontWeight: 'bold',
  },
  subscriptionDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
    marginBottom: '12px',
    fontSize: '14px',
  },
  cancelInfo: {
    marginTop: '8px',
    padding: '8px',
    backgroundColor: colors.background,
    borderRadius: '4px',
    fontSize: '13px',
  },
  invoices: {
    marginTop: '12px',
    fontSize: '14px',
  },
  invoice: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '4px 0',
    borderBottom: `1px solid ${colors.border}`,
  },
  cancelBtn: {
    marginTop: '12px',
    padding: '8px 16px',
    backgroundColor: colors.error,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  modalOverlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
  },
  cancelModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    width: '400px',
    maxWidth: '90vw',
  },
  cancelTitle: {
    margin: '0 0 16px 0',
    color: colors.primary,
  },
  textarea: {
    width: '100%',
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    marginBottom: '16px',
  },
  cancelButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  confirmBtn: {
    padding: '8px 16px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  addModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    width: '400px',
    maxWidth: '90vw',
  },
  addTitle: {
    margin: '0 0 20px 0',
    color: colors.primary,
  },
  formGroup: {
    marginBottom: '16px',
  },
  input: {
    width: '100%',
    padding: '8px',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    marginTop: '4px',
  },
  addButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
  cancelAddBtn: {
    padding: '8px 16px',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  saveBtn: {
    padding: '8px 16px',
    backgroundColor: colors.success,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  editBtn: {
    marginTop: '12px',
    marginRight: '8px',
    padding: '8px 16px',
    backgroundColor: colors.primary,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  editModal: {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    width: '400px',
    maxWidth: '90vw',
  },
  editTitle: {
    margin: '0 0 20px 0',
    color: colors.primary,
  },
  editButtons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '20px',
  },
  cancelEditBtn: {
    padding: '8px 16px',
    backgroundColor: colors.border,
    color: colors.text,
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  saveEditBtn: {
    padding: '8px 16px',
    backgroundColor: colors.success,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
};

export default CandidateSubscriptionsModal;